// src/accounts/components/RegisterProjectModal.jsx
//
// "Register Project" flow. Launched from ProjectSwitcher's "New project" row,
// from ProjectsPage, from Draft SOW's "+ New Project" shortcut (Step 2, once
// an Opportunity is selected), and from SOW Analysis's "+ New Project". The
// Account this Project registers under is implicit (whichever Account is
// currently selected) — shown read-only in the header so it's unambiguous.
//
// Asks for the project name, its delivery platform, and (unless the caller
// already knows one — see presetContractType below) its contract type:
// CreateEngagementRequest (presentation/conductor/schemas/
// create_engagement_request.py) takes client_name, platform, contract_type,
// and opportunity_id (account_id is filled in from the selected Account,
// not this form). account_manager_id comes from claims.user_id server-side —
// the creator is auto-added to the team as its account manager
// (CreateEngagementHandler._add_creator_as_admin) — so there is no
// "account manager" or "team members" input for the backend to receive;
// the previous fields here were collected but silently discarded.
//
// platform, contract_type, and opportunity_id are set once, here, at
// creation, and never editable afterward — there is no update route for
// these or any other Engagement field.
//
// Opportunity linking: when the caller already knows the Opportunity (Draft
// SOW, where one is already selected before "+ New Project" is clicked),
// pass presetOpportunityId/presetContractType and this modal shows them
// read-only, carried over. Otherwise (ProjectsPage, SOW Analysis --
// no Opportunity already in view) this modal offers its own optional
// "Link to an Opportunity" picker, fetching the list itself, so a Project
// can still be tied to one at creation time without the caller needing to
// build a picker of its own.

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CheckCircle2, Building2 } from "lucide-react";
import { Modal, FormField, Input, Select, Button, Alert, useDiscardConfirm } from "../../shared/ui";
import { sowDraftApi } from "../../api/client";

const PLATFORM_OPTIONS = [
  { value: "orion", label: "Orion" },
  { value: "oracle", label: "Oracle" },
  { value: "sfdc", label: "SFDC" },
];

const CONTRACT_TYPE_OPTIONS = [
  { value: "T&M", label: "T&M" },
  { value: "Fixed Bid", label: "Fixed Bid" },
  { value: "Managed Services", label: "Managed Services" },
];

const initialValues = { name: "", platform: "orion", contractType: "T&M" };

export default function RegisterProjectModal({
  open,
  accountName,
  presetContractType,
  presetOpportunityId,
  onClose,
  onRegister,
}) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [dirty, setDirty] = useState(false);

  const [opportunities, setOpportunities] = useState([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState("");

  const showOpportunityPicker = open && !presetOpportunityId;

  useEffect(() => {
    if (!showOpportunityPicker) return;

    let cancelled = false;
    (async () => {
      setOpportunitiesLoading(true);
      try {
        const resp = await sowDraftApi.listOpps(1, 100);
        if (!cancelled) setOpportunities(resp?.items || []);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setOpportunitiesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showOpportunityPicker]);

  const linkedOpportunity = selectedOpportunityId
    ? opportunities.find((o) => String(o.id) === selectedOpportunityId) || null
    : null;

  // Free text on the backend (Engagement.contract_type has no enum -- see
  // this file's own top comment), so the Opportunity's raw value is carried
  // over as-is rather than mapped onto CONTRACT_TYPE_OPTIONS.
  const carriedOverContractType = presetContractType || linkedOpportunity?.contract_type || null;

  function reset() {
    setValues(initialValues);
    setErrors({});
    setSubmitting(false);
    setSuccess(null);
    setSelectedOpportunityId("");
    setDirty(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty && !success, handleClose);

  function setField(name, v) {
    setValues((prev) => ({ ...prev, [name]: v }));
    setDirty(true);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!values.name.trim()) {
      setErrors({ name: "Project name is required." });
      return;
    }

    setSubmitting(true);
    try {
      const project = await onRegister({
        name: values.name.trim(),
        platform: values.platform,
        contractType: carriedOverContractType || values.contractType,
        opportunityId: presetOpportunityId || linkedOpportunity?.id || undefined,
      });
      setSuccess(project);
    } catch (err) {
      if (err?.fieldErrors) {
        setErrors(err.fieldErrors);
      } else {
        setErrors({ name: err?.message || "Registration failed. Please try again." });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal open={open} title="Register Project" onClose={requestClose}>
        {success ? (
          <div className="flex flex-col gap-4">
            <Alert variant="success" icon={CheckCircle2} title={`"${success.name}" registered`}>
              It's now selected in the Project switcher under {accountName}.
            </Alert>
            <div className="flex justify-end">
              <Button variant="primary" onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="flex items-center gap-2 rounded-lg bg-brand-50 border border-brand-100 px-3 py-2 text-sm text-ink">
              <Building2 size={15} className="text-brand-700 shrink-0" />
              Registering under <span className="font-semibold">{accountName || "—"}</span>
            </div>

            <FormField label="Project name" required error={Boolean(errors.name)} errorText={errors.name}>
              <Input
                value={values.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. Meridian Support"
                autoFocus
              />
            </FormField>

            <FormField label="Delivery platform" required>
              <Select
                options={PLATFORM_OPTIONS}
                value={values.platform}
                onChange={(e) => setField("platform", e.target.value)}
              />
            </FormField>

            {presetOpportunityId ? (
              <p className="text-xs text-muted">Linked to the selected opportunity.</p>
            ) : showOpportunityPicker ? (
              <FormField label="Link to an Opportunity" helperText="Optional -- carries the Opportunity's contract type onto this Project">
                <Select
                  options={[
                    { value: "", label: opportunitiesLoading ? "Loading opportunities…" : "None" },
                    ...opportunities.map((o) => ({
                      value: String(o.id),
                      label: o.salesforce_opportunity_id ? `${o.name} — ${o.salesforce_opportunity_id}` : o.name,
                    })),
                  ]}
                  value={selectedOpportunityId}
                  onChange={(e) => {
                    setSelectedOpportunityId(e.target.value);
                    setDirty(true);
                  }}
                  disabled={opportunitiesLoading}
                />
              </FormField>
            ) : null}

            {carriedOverContractType ? (
              <p className="text-xs text-muted">
                Contract type: <span className="font-medium text-ink">{carriedOverContractType}</span> (carried over
                from the {presetOpportunityId ? "selected" : "linked"} opportunity)
              </p>
            ) : (
              <FormField label="Contract type" required>
                <Select
                  options={CONTRACT_TYPE_OPTIONS}
                  value={values.contractType}
                  onChange={(e) => setField("contractType", e.target.value)}
                />
              </FormField>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
              <Button type="submit" variant="primary" loading={submitting}>Register Project</Button>
            </div>
          </form>
        )}
      </Modal>
      {confirmDialog}
    </>
  );
}

RegisterProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  accountName: PropTypes.string,
  presetContractType: PropTypes.string,
  presetOpportunityId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onRegister: PropTypes.func.isRequired,
};
